import React from "react";
import { Link } from "react-router-dom";

// Renders the app's plain-text legal documents (see src/content/) without rewording them.
//
// Format, as used in the app's LegalModal:
//   line 1                    -> page title (h1)
//   "Last updated: ..." etc.  -> date pills
//   first paragraph(s) before the first heading -> intro
//   ALL-CAPS line             -> section heading (h2), e.g. "4. AI PROCESSING (OPENAI)"
//   "2.1 Your account"        -> sub-heading (h3)
//   "• item"                  -> bullet list
//   anything else             -> paragraph, one per line
//
// Email addresses, ico.org.uk and mentions of the other legal pages become links.

const isDateLine = (line) => /^(Last updated|Effective date):/.test(line);
const isSubHeading = (line) => /^\d+\.\d+[a-z]?\s/.test(line); // also "2.3a ..."
const isHeading = (line) => /[A-Z]/.test(line) && line === line.toUpperCase() && !line.startsWith("•");
const isBullet = (line) => line.startsWith("• ");

const headingId = (line) => {
  const number = line.match(/^(\d+)\./);
  if (number) return `section-${number[1]}`;
  return line.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
};

const DEFAULT_LINKS = [
  { text: "support@thryvin.app", href: "mailto:support@thryvin.app" },
  { text: "ico.org.uk", href: "https://ico.org.uk" },
  { text: "Community Guidelines", to: "/community-guidelines" },
  { text: "Privacy Policy", to: "/privacy" },
];

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const linkify = (text, links, keyPrefix) => {
  const pattern = new RegExp(`(${links.map((l) => escapeRegExp(l.text)).join("|")})`, "g");
  return text.split(pattern).map((part, i) => {
    const link = links.find((l) => l.text === part);
    if (!link) return part;
    const key = `${keyPrefix}-${i}`;
    if (link.to) {
      return (
        <Link key={key} to={link.to} className="inline-link">
          {part}
        </Link>
      );
    }
    const external = link.href.startsWith("http");
    return (
      <a
        key={key}
        href={link.href}
        className="inline-link"
        {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      >
        {part}
      </a>
    );
  });
};

const PlainTextDocument = ({ text, excludeLinks = [] }) => {
  const links = DEFAULT_LINKS.filter((l) => !excludeLinks.includes(l.to || l.href));
  const lines = text.replace(/\r\n/g, "\n").split("\n");
  const [title, ...rest] = lines;

  const dates = [];
  const intro = [];
  const sections = []; // { heading, blocks: [] }
  let bullets = null;

  const blocks = () => (sections.length ? sections[sections.length - 1].blocks : intro);
  const closeList = () => {
    if (bullets) blocks().push({ type: "ul", items: bullets });
    bullets = null;
  };

  rest.forEach((raw) => {
    const line = raw.trim();
    if (!line) {
      closeList();
      return;
    }
    if (!sections.length && !intro.length && isDateLine(line)) {
      dates.push(line);
      return;
    }
    if (isBullet(line)) {
      bullets = bullets || [];
      bullets.push(line.slice(2));
      return;
    }
    closeList();
    if (isSubHeading(line)) blocks().push({ type: "h3", text: line });
    else if (isHeading(line)) sections.push({ heading: line, blocks: [] });
    else blocks().push({ type: "p", text: line });
  });
  closeList();

  const renderBlock = (block, key) => {
    if (block.type === "ul") {
      return (
        <ul key={key}>
          {block.items.map((item, i) => (
            <li key={i}>{linkify(item, links, `${key}-${i}`)}</li>
          ))}
        </ul>
      );
    }
    const Tag = block.type;
    return <Tag key={key}>{linkify(block.text, links, key)}</Tag>;
  };

  return (
    <>
      <h1>{title.trim()}</h1>
      {dates.map((d) => (
        <p key={d} className="last-updated">
          {d}
        </p>
      ))}
      {intro.length > 0 && (
        <div className="intro">{intro.map((block, i) => renderBlock(block, `intro-${i}`))}</div>
      )}
      {sections.map((section) => {
        const id = headingId(section.heading);
        return (
          <section key={id} aria-labelledby={id}>
            <h2 id={id} className="caps-heading">
              {section.heading}
            </h2>
            {section.blocks.map((block, i) => renderBlock(block, `${id}-${i}`))}
          </section>
        );
      })}
    </>
  );
};

export default PlainTextDocument;
